import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardTitle, ActionButton } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { getPendingTeachers, approveTeacher, rejectTeacher } from '../../services/adminApi';
import toast from 'react-hot-toast';
import { User } from '../../types';

const TableContainer = styled(Card)`
  grid-column: 1 / -1;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.secondary};
  font-weight: 600;
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  color: ${glassTheme.colors.text.primary};
`;

const StatusBadge = styled.span<{ status: 'pending' | 'approved' | 'rejected' }>`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'approved': return 'rgba(0, 255, 0, 0.2)';
      case 'pending': return 'rgba(255, 255, 0, 0.2)';
      case 'rejected': return 'rgba(255, 0, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return '#00FF00';
      case 'pending': return '#FFFF00';
      case 'rejected': return '#FF0000';
      default: return '#888888';
    }
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${glassTheme.colors.text.muted};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ApproveButton = styled(ActionButton)`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid rgba(0, 255, 0, 0.5);
  color: #00FF00;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
  }
`;

const RejectButton = styled(ActionButton)`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid rgba(255, 0, 0, 0.5);
  color: #FF0000;

  &:hover {
    background: rgba(255, 0, 0, 0.3);
  }
`;

export const TeacherApproval: React.FC = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    loadPendingTeachers();
  }, []);

  const loadPendingTeachers = async () => {
    try {
      setLoading(true);
      const data = await getPendingTeachers();
      setTeachers(data);
    } catch (error) {
      console.error('Error loading pending teachers:', error);
      toast.error('Failed to load pending teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (teacherId: string) => {
    try {
      setProcessing(teacherId);
      await approveTeacher(teacherId);
      toast.success('Teacher approved successfully');
      // Remove from list
      setTeachers(teachers.filter(t => t.id === teacherId));
    } catch (error: any) {
      console.error('Error approving teacher:', error);
      toast.error(error.response?.data?.message || 'Failed to approve teacher');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (teacherId: string) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return; // User cancelled

    try {
      setProcessing(teacherId);
      await rejectTeacher(teacherId, reason || undefined);
      toast.success('Teacher rejected successfully');
      // Remove from list
      setTeachers(teachers.filter(t => t.id === teacherId));
    } catch (error: any) {
      console.error('Error rejecting teacher:', error);
      toast.error(error.response?.data?.message || 'Failed to reject teacher');
    } finally {
      setProcessing(null);
    }
  };

  const getTeacherId = (teacher: User): string => {
    return teacher.id || '';
  };

  return (
    <DashboardLayout
      title="教师审核"
      subtitle="审核教师注册申请"
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>加载中...</div>
      ) : teachers.length === 0 ? (
        <TableContainer>
          <EmptyState>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <div>暂无待审核的教师申请</div>
          </EmptyState>
        </TableContainer>
      ) : (
        <>
          <Grid>
            <Card style={{ gridColumn: '1 / -1' }}>
              <CardTitle>待审核教师 ({teachers.length})</CardTitle>
            </Card>
          </Grid>

          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <TableHeader>姓名</TableHeader>
                  <TableHeader>邮箱</TableHeader>
                  <TableHeader>所属院校</TableHeader>
                  <TableHeader>院系/部门</TableHeader>
                  <TableHeader>注册时间</TableHeader>
                  <TableHeader>操作</TableHeader>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <TableRow key={getTeacherId(teacher)}>
                    <TableCell>{teacher.name}</TableCell>
                    <TableCell>{teacher.email}</TableCell>
                    <TableCell>{teacher.teacherInfo?.institution || '-'}</TableCell>
                    <TableCell>{teacher.teacherInfo?.department || '-'}</TableCell>
                    <TableCell>{new Date(teacher.createdAt).toLocaleDateString('zh-CN')}</TableCell>
                    <TableCell>
                      <ButtonGroup>
                        <ApproveButton
                          onClick={() => handleApprove(getTeacherId(teacher))}
                          disabled={processing === getTeacherId(teacher)}
                        >
                          {processing === getTeacherId(teacher) ? '处理中...' : '通过'}
                        </ApproveButton>
                        <RejectButton
                          onClick={() => handleReject(getTeacherId(teacher))}
                          disabled={processing === getTeacherId(teacher)}
                        >
                          拒绝
                        </RejectButton>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </>
      )}
    </DashboardLayout>
  );
};
